/**
 * GPU-accelerated 3D SDF deform: the spatial warp
 *
 *   sdf_deformed(p) = sdf_original(p - w * v)
 *
 * with v = mouse - mouse_start (mm) and w = bell(t) of the
 * ellipsoidal distance t = sqrt((s/Ta)² + (ρ/Tl)²), s along
 * v and ρ lateral. The falloff lengths Ta and Tl are sized
 * by the caller to keep the warp fold-free (see
 * DeformAction).
 *
 * Distances are evaluated in patient mm so the kernel shape
 * is correct on rotated and anisotropic grids. In 2D mode
 * the warp is identity outside the deformed view's current
 * slice.
 */

import { ReadonlyMat4, ReadonlyVec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { StrokeMode } from '~/state/atoms/toolsAtoms';
import { SDF_DEFAULT_MARGIN_MM } from './sdfConstants';
import { resetGlDefaults } from './utils';

class DeformShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;
        in vec2 position;
        out vec2 vUV;
        void main() {
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    // w = bell(t): C¹ falloff from 1 at the cursor to 0 at
    // t = 1 — parabolic cap (no cusp at the tip), straight
    // flank, parabolic ease-out (no crease at the edge).
    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        in vec2 vUV;
        out vec4 fragColor;

        uniform sampler3D uOriginalTex;
        uniform vec3 uTexSize;       // (nx, ny, nz)
        uniform float uSliceZ;       // current slice's voxel-centred Z
        uniform mat4 uVoxelToWorld;  // voxel -> patient mm
        uniform mat4 uWorldToVoxel;  // patient mm -> voxel
        uniform vec3 uMouseMm;       // current mouse position (mm)
        uniform vec3 uVMm;           // mouse - mouse_start (mm)
        // Falloff lengths of the ellipsoidal kernel (mm).
        uniform float uAxialFalloffMm;
        uniform float uLateralFalloffMm;

        // Fractions of the falloff taken by the rounded cap
        // and the ease-out base. They fix SLOPE, the bell's
        // steepest slope; DeformAction's
        // AXIAL_FALLOFF_PER_STROKE_MM must exceed SLOPE to
        // keep the warp fold-free.
        const float CAP = 0.2;
        const float BASE = 0.4;
        const float SLOPE = 1.0 / (1.0 - 0.5 * (CAP + BASE));

        float bell(float t) {
            if (t <= CAP) {
                return 1.0 - SLOPE * t * t / (2.0 * CAP);
            }
            if (t < 1.0 - BASE) {
                return 1.0 - SLOPE * (t - 0.5 * CAP);
            }
            float u = 1.0 - t;
            return SLOPE * u * u / (2.0 * BASE);
        }
        // 1 when the deform is confined to the deformed
        // view's current slice, 0 when it is 3D.
        uniform int uMode2d;
        // Deformed view's plane normal (unit, patient space).
        uniform vec3 uViewDirection;
        // Thickness (mm) of the slab, centred on the deformed
        // view's plane, that a 2D deform is confined to.
        uniform float uSlabThicknessMm;

        void main() {
            vec3 voxelPos = vec3(vUV * uTexSize.xy, uSliceZ);
            vec3 pMm = (uVoxelToWorld * vec4(voxelPos, 1.0)).xyz;

            vec3 dMm = pMm - uMouseMm;
            float vLen = length(uVMm);
            vec3 axis = vLen > 1e-6
                ? uVMm / vLen
                : vec3(0.0, 0.0, 0.0);
            float s = dot(dMm, axis);
            float latSq = max(dot(dMm, dMm) - s * s, 0.0);
            float sN = s / uAxialFalloffMm;
            float latN2 =
                latSq / (uLateralFalloffMm * uLateralFalloffMm);
            float t = clamp(sqrt(sN * sN + latN2), 0.0, 1.0);
            float w = bell(t);

            // In 2D mode the warp is identity (w = 0, the
            // sample lands on the voxel's own position) for
            // voxels whose offset from the cursor along the
            // plane normal exceeds half the slab thickness.
            if (uMode2d == 1) {
                float offMm = abs(dot(dMm, uViewDirection));
                if (offMm > 0.5 * uSlabThicknessMm) {
                    w = 0.0;
                }
            }

            vec3 sampleMm = pMm - uVMm * w;
            vec3 sampleVoxel = (uWorldToVoxel * vec4(sampleMm, 1.0)).xyz;
            vec3 sampleUV = sampleVoxel / uTexSize;

            // Intersect (min) with the texture box's own SDF
            // so the surface keeps its gradient band where
            // cut by the texture boundary.
            vec3 resMm = vec3(
                length(uVoxelToWorld[0].xyz),
                length(uVoxelToWorld[1].xyz),
                length(uVoxelToWorld[2].xyz));
            vec3 faceDistMm =
                min(sampleVoxel, uTexSize - sampleVoxel) * resMm;
            float boundaryDistMm =
                min(faceDistMm.x, min(faceDistMm.y, faceDistMm.z));
            float boundaryV = clamp(
                0.5 + 0.5 * boundaryDistMm
                    / ${SDF_DEFAULT_MARGIN_MM.toFixed(1)},
                0.0,
                1.0);

            float v = min(texture(uOriginalTex, sampleUV).r, boundaryV);
            fragColor = vec4(v, v, v, 1.0);
        }`;

    public readonly uTexSize: UniformVec3;
    public readonly uSliceZ: UniformFloat;
    public readonly uVoxelToWorld: UniformMatrix;
    public readonly uWorldToVoxel: UniformMatrix;
    public readonly uMouseMm: UniformVec3;
    public readonly uVMm: UniformVec3;
    public readonly uAxialFalloffMm: UniformFloat;
    public readonly uLateralFalloffMm: UniformFloat;
    public readonly uMode2d: UniformInt;
    public readonly uViewDirection: UniformVec3;
    public readonly uSlabThicknessMm: UniformFloat;

    public constructor() {
        super(
            DeformShader.VERTEX_CONTENT,
            DeformShader.FRAGMENT_CONTENT,
            'position'
        );

        this.uTexSize = new UniformVec3('uTexSize');
        this.uSliceZ = new UniformFloat('uSliceZ');
        this.uVoxelToWorld = new UniformMatrix('uVoxelToWorld');
        this.uWorldToVoxel = new UniformMatrix('uWorldToVoxel');
        this.uMouseMm = new UniformVec3('uMouseMm');
        this.uVMm = new UniformVec3('uVMm');
        this.uAxialFalloffMm = new UniformFloat('uAxialFalloffMm');
        this.uLateralFalloffMm = new UniformFloat('uLateralFalloffMm');
        this.uMode2d = new UniformInt('uMode2d');
        this.uViewDirection = new UniformVec3('uViewDirection');
        this.uSlabThicknessMm = new UniformFloat('uSlabThicknessMm');

        super.storeUniformLocations(
            this.uTexSize,
            this.uSliceZ,
            this.uVoxelToWorld,
            this.uWorldToVoxel,
            this.uMouseMm,
            this.uVMm,
            this.uAxialFalloffMm,
            this.uLateralFalloffMm,
            this.uMode2d,
            this.uViewDirection,
            this.uSlabThicknessMm
        );

        const samplerLocation = gl.getUniformLocation(
            this.programID,
            'uOriginalTex'
        );
        this.start();
        gl.uniform1i(samplerLocation, 0); // texture unit 0
        this.stop();
    }
}

/**
 * Frame-time renderer that warps the SDF slice-by-slice on
 * the GPU. Owns the shader; the FBO and the snapshot
 * texture are owned by the caller (one per stroke).
 */
export class DeformRenderer implements Renderer {
    private readonly shader: DeformShader;

    public constructor() {
        this.shader = new DeformShader();
    }

    public delete(): void {
        this.shader.delete();
    }

    /**
     * Render the warped slices [zMin..zMax] from
     * `originalTex` into `dstTex`, restricted to the texels
     * [xMin..xMax] × [yMin..yMax] of each slice. Both
     * textures must be R8 3D textures of size `texSize`.
     * In 2D mode the warp is identity outside the slab of
     * `slabThicknessMm` centred on the view plane (through
     * `mouseMm`, normal `viewDirection`).
     */
    public renderRange(
        originalTex: WebGLTexture,
        dstTex: WebGLTexture,
        fbo: FrameBufferObject,
        texSize: ReadonlyVec3,
        voxelToWorld: ReadonlyMat4,
        worldToVoxel: ReadonlyMat4,
        mouseMm: ReadonlyVec3,
        vMm: ReadonlyVec3,
        axialFalloffMm: number,
        lateralFalloffMm: number,
        mode: StrokeMode,
        viewDirection: ReadonlyVec3,
        slabThicknessMm: number,
        xMin: number,
        xMax: number,
        yMin: number,
        yMax: number,
        zMin: number,
        zMax: number
    ): void {
        const nx = texSize[0];
        const ny = texSize[1];
        const nz = texSize[2];
        if (
            xMin > xMax ||
            yMin > yMax ||
            zMin > zMax ||
            nx === 0 ||
            ny === 0 ||
            nz === 0
        ) {
            return;
        }

        this.shader.start();
        this.shader.uTexSize.loadVec3(nx, ny, nz);
        this.shader.uVoxelToWorld.loadMatrix(voxelToWorld);
        this.shader.uWorldToVoxel.loadMatrix(worldToVoxel);
        this.shader.uMouseMm.loadVec3(mouseMm);
        this.shader.uVMm.loadVec3(vMm);
        this.shader.uAxialFalloffMm.loadFloat(axialFalloffMm);
        this.shader.uLateralFalloffMm.loadFloat(lateralFalloffMm);
        this.shader.uMode2d.loadInt(mode === '2d' ? 1 : 0);
        this.shader.uViewDirection.loadVec3(viewDirection);
        this.shader.uSlabThicknessMm.loadFloat(slabThicknessMm);

        gl.viewport(0, 0, nx, ny);
        RenderStorage.getQuadVAO().bind(0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, originalTex);

        // Only texels inside the kernel box can change; skip
        // rasterising the rest of each slice.
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(xMin, yMin, xMax - xMin + 1, yMax - yMin + 1);
        try {
            for (let z = zMin; z <= zMax; z++) {
                this.shader.uSliceZ.loadFloat(z + 0.5);
                fbo.bind();
                fbo.createTextureLayer(dstTex, z);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        } finally {
            gl.disable(gl.SCISSOR_TEST);
        }

        RenderStorage.getQuadVAO().unbind(0);
        this.shader.stop();
        gl.bindTexture(gl.TEXTURE_3D, null);
        resetGlDefaults();
    }
}
